import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "../hooks/useAuth";
import { Plus, Search, Loader2, Edit, Trash2, Home, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

interface Property {
  id: string;
  nome: string | null;
  codigo: string;
  tipoImovelId: string;
  logradouro: string;
  numero: string;
  complemento: string | null;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  areaUtil: number | null;
  areaTotal: number | null;
  quartos: number | null;
  banheiros: number | null;
  vagasGaragem: number | null;
  valorAluguel: number;
  valorCondominio: number | null;
  valorIptu: number | null;
  descricao: string | null;
  ativo: boolean;
  createdAt: string;
}

export default function Properties() {
  const { user } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Property | null>(null);

  useEffect(() => {
    fetchProperties();
  }, [user]);

  const fetchProperties = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      
      const res = await fetch(`${API_BASE}/api/imoveis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setProperties(data.imoveis || []);
      }
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/imoveis/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Imóvel excluído");
        fetchProperties();
      } else {
        toast.error("Não foi possível excluir o imóvel");
      }
    } catch {
      toast.error("Erro de conexão");
    } finally {
      setPendingDelete(null);
    }
  };

  if (!user) return <div className="flex items-center justify-center h-screen">Carregando...</div>;

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const filteredProperties = properties.filter(p =>
    p.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.nome || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.logradouro.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.bairro.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.cidade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Imóveis</h1>
          <Button onClick={() => window.location.href = "/properties/new"}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Imóvel
          </Button>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar imóveis..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {filteredProperties.length === 0 ? (
              <div className="text-center py-12">
                <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? "Nenhum imóvel encontrado." : "Nenhum imóvel cadastrado ainda."}
                </p>
                {!searchTerm && (
                  <Button onClick={() => window.location.href = "/properties/new"}>
                    <Plus className="mr-2 h-4 w-4" />
                    Cadastrar Primeiro Imóvel
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Imóvel</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Endereço</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Valor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredProperties.map((property) => (
                      <tr key={property.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4">
                          <p className="font-medium">{property.nome || property.logradouro}</p>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{property.codigo}</span>
                        </td>
                        <td className="px-6 py-4">
                          <p>{property.logradouro}, {property.numero}</p>
                          <p className="text-sm text-muted-foreground">{property.bairro}, {property.cidade}/{property.uf}</p>
                        </td>
                        <td className="px-6 py-4">{property.tipoImovelId}</td>
                        <td className="px-6 py-4 font-medium">{formatCurrency(property.valorAluguel)}</td>
                        <td className="px-6 py-4">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            property.ativo 
                              ? "bg-green-100 text-green-800" 
                              : "bg-gray-100 text-gray-800"
                          )}>
                            {property.ativo ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" title="Ver despesas do imóvel" onClick={() => window.location.href = `/properties/${property.id}/despesas`}>
                              <Receipt className="h-4 w-4" />
                              <span className="sr-only">Despesas</span>
                            </Button>
                            <span className="text-xs text-muted-foreground opacity-80">Despesas</span>
                            <Button variant="ghost" size="icon" title="Editar" onClick={() => window.location.href = `/properties/${property.id}/edit`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Excluir" onClick={() => setPendingDelete(property)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <ConfirmDialog
          open={!!pendingDelete}
          title="Excluir imóvel"
          description={`Tem certeza que deseja excluir "${pendingDelete?.nome || pendingDelete?.codigo}"? Esta ação não pode ser desfeita.`}
          confirmText="Excluir"
          destructive
          onConfirm={() => pendingDelete && handleDelete(pendingDelete.id)}
          onCancel={() => setPendingDelete(null)}
        />
      </div>
    </div>
  );
}
